/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { ContentSliderAssetFragment } from "@cosmos/hygraph-schema";

import {
	Animate,
	BackgroundContainer,
	CosmosTagProps,
	ResponsiveContainer,
	SectionContainer,
	SectionContainerProps,
	SectionHeading,
	Wrap,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { Flex, VStack } from "@panda/jsx";

import { ContentSliderAssetMedia } from "./ContentSliderAsset.Media";
import { ContentLargeImageMainStack } from "../content-large-image/ContentLargeImage.MainStack";

export interface ContentSliderAssetSectionProps extends Omit<ContentSliderAssetFragment, "badges"> {
	className?: string;
	sectionIndex?: number;
	enableAnimate?: boolean;
	badges?: CosmosTagProps[] | null;
	date?: string | null;
	sectionContainerProps?: SectionContainerProps;
}

export const ContentSliderAssetSection = (props: ContentSliderAssetSectionProps | null): React.JSX.Element | null => {
	const validHeading = Boolean(props?.config?.heading?.title || props?.config?.heading?.desc);
	const validContent = Boolean(
		props?.eyebrow ||
			props?.title ||
			props?.desc ||
			props?.buttons?.length ||
			props?.badges?.length ||
			props?.date ||
			props?.assets?.length,
	);
	if (!validHeading && !validContent) return null;
	const enableAnimate = props?.enableAnimate !== undefined ? props.enableAnimate : true;
	const isFirstSection = props?.sectionIndex === 0;
	const mainStackTitleAs = isFirstSection
		? props?.config?.heading?.title?.text
			? "h2"
			: "h1"
		: props?.config?.heading?.title?.text
			? "h3"
			: "h2";

	return (
		<BackgroundContainer
			bgAnimation={props?.config?.backgroundAnimation}
			bgImageAsset={props?.config?.backgroundImage}
			bgFullHeight={props?.config?.backgroundFullHeight}
			edsBackgroundColor={props?.config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer contentVisibility="unset" {...props?.sectionContainerProps}>
					<VStack
						alignItems="stretch"
						gap="eds.dimension.56"
						className={cx("content-large-image-section", props?.className)}
					>
						{validHeading ? (
							<SectionHeading
								{...props?.config?.heading}
								title={{ ...props?.config?.heading?.title, as: isFirstSection ? "h1" : "h2" }}
							/>
						) : null}
						{validContent ? (
							<Flex
								direction={{ base: props?.flip ? "row-reverse" : "row", lgDown: "column" }}
								justifyContent="space-between"
								columnGap="eds.dimension.64"
								rowGap={{ md: "eds.dimension.80", mdDown: "eds.dimension.48" }}
								className={cx("section-flex")}
							>
								<ContentLargeImageMainStack
									animate={enableAnimate ? isFirstSection : false}
									eyebrow={props?.eyebrow || ""}
									gradient={props?.gradient}
									title={props?.title}
									titleAs={mainStackTitleAs}
									desc={props?.desc}
									buttons={props?.buttons || []}
									badges={props?.badges || []}
									date={props?.date}
								/>
								{props?.assets?.length ? (
									<div
										className={cx(
											"asset-wrap",
											css({
												aspectRatio: "16 / 9",
												lg: {
													w: "1/2",
												},
												"--asset-border-radius": `24px`,
											}),
										)}
									>
										<Wrap
											condition={enableAnimate ? isFirstSection : false}
											wrap={(children) => (
												<Animate delay={0.5} type="slideLeft" customCss={css.raw({ flexBasis: "50%" })}>
													{children}
												</Animate>
											)}
										>
											<ContentSliderAssetMedia assets={props?.assets} />
										</Wrap>
									</div>
								) : null}
							</Flex>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
